import React from 'react';

/**
 * The house pieces, as the Issues Log, Workstream and Audit pages use them:
 * white rounded cards with a soft shadow, slate text, orange primary actions,
 * pill tabs, soft rounded status pills.
 */
export function Card({ title, subtitle, right, children, className = '', padding = 'p-5', shadow = 'shadow-sm' }) {
  return (
    <section className={`bg-white rounded-2xl border border-slate-200 ${shadow} ${padding} ${className}`}>
      {(title || right) && (
        <div className={`flex items-start justify-between gap-4 ${padding === 'p-0' ? 'px-5 py-4 border-b border-slate-200' : 'mb-4'}`}>
          <div className="min-w-0">
            {title && <h3 className="text-sm font-semibold text-slate-900">{title}</h3>}
            {subtitle && <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>}
          </div>
          {right && <div className="flex-shrink-0 flex items-center gap-2 flex-wrap justify-end">{right}</div>}
        </div>
      )}
      {children}
    </section>
  );
}

/** The Issues Log pill tab group. tabs: [[value, label, count?]] */
export function TabBar({ value, onChange, tabs, className = '' }) {
  return (
    <div className={`inline-flex rounded-xl bg-slate-100 p-1 gap-0.5 ${className}`}>
      {tabs.map(([v, l, count]) => (
        <button key={v} type="button" onClick={() => onChange(v)}
          className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition flex items-center gap-1.5 ${value === v ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
          {l}
          {typeof count === 'number' && (
            <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${value === v ? 'bg-slate-100 text-slate-700' : 'bg-slate-200/70 text-slate-500'}`}>{count}</span>
          )}
        </button>
      ))}
    </div>
  );
}

/** Workstream's numbered step chips. steps: [label]; current is 0 based. */
export function StepChips({ steps, current, onJump }) {
  return (
    <div className="flex items-center gap-2 flex-wrap">
      {steps.map((label, i) => {
        const active = i === current;
        const done = i < current;
        const cls = active ? 'bg-blue-50 border-blue-300 text-blue-800' : done ? 'bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100 cursor-pointer' : 'bg-slate-100 border-slate-200 text-slate-400';
        const bubble = active ? 'bg-blue-600 text-white' : done ? 'bg-emerald-500 text-white' : 'bg-slate-200 text-slate-500';
        return (
          <div key={label} className="flex items-center gap-2">
            <button type="button" onClick={() => done && onJump && onJump(i)}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold transition border ${cls}`}>
              <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${bubble}`}>{i + 1}</span>
              {label}
            </button>
            {i < steps.length - 1 && <span className="text-slate-300">|</span>}
          </div>
        );
      })}
    </div>
  );
}

export function Stat({ label, value, tone = 'slate', hint }) {
  const tones = { slate: 'text-slate-900', red: 'text-rose-600', rose: 'text-rose-600', emerald: 'text-emerald-600', amber: 'text-amber-600', orange: 'text-orange-600' };
  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm px-4 py-3 min-w-0">
      <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 truncate">{label}</div>
      <div className={`text-2xl font-bold mt-1 tabular-nums tracking-tight ${tones[tone] || tones.slate}`}>{value}</div>
      {hint && <div className="text-[11px] text-slate-500 mt-0.5">{hint}</div>}
    </div>
  );
}

const PILL = {
  slate: 'bg-slate-100 text-slate-600',
  red: 'bg-rose-50 text-rose-700',
  rose: 'bg-rose-50 text-rose-700',
  amber: 'bg-amber-50 text-amber-700',
  emerald: 'bg-emerald-50 text-emerald-700',
  blue: 'bg-sky-50 text-sky-700',
  sky: 'bg-sky-50 text-sky-700',
  orange: 'bg-orange-50 text-orange-700',
  violet: 'bg-violet-50 text-violet-700',
};
export function Pill({ tone = 'slate', children, className = '' }) {
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10.5px] font-semibold whitespace-nowrap ${PILL[tone] || PILL.slate} ${className}`}>{children}</span>;
}

export const btn = {
  // The Issues Log "+ Add issue" button.
  primary: 'px-5 py-2.5 rounded-xl bg-gradient-to-r from-orange-600 to-amber-500 text-white text-sm font-semibold hover:opacity-90 disabled:opacity-50',
  // The Audit "Start an audit" button, for rows and dialogs.
  small: 'px-3.5 py-2 rounded-xl bg-orange-500 hover:bg-orange-600 text-white text-xs font-semibold disabled:opacity-50',
  secondary: 'px-4 py-2 rounded-xl border border-slate-200 bg-white text-slate-700 text-sm font-semibold hover:bg-slate-50 disabled:opacity-50',
  secondarySmall: 'px-3 py-1.5 rounded-xl border border-slate-200 bg-white text-slate-700 text-xs font-semibold hover:bg-slate-50 disabled:opacity-50',
  success: 'px-4 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold disabled:opacity-50',
  danger: 'px-4 py-2 rounded-xl border border-rose-200 bg-white text-rose-700 text-sm font-semibold hover:bg-rose-50 disabled:opacity-50',
  dangerSmall: 'px-3 py-1.5 rounded-xl border border-rose-200 bg-white text-rose-700 text-xs font-semibold hover:bg-rose-50 disabled:opacity-50',
  link: 'text-xs font-semibold text-orange-700 hover:text-orange-900',
};

export const input = 'w-full rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-800 bg-white focus:outline-none focus:ring-2 focus:ring-orange-200 focus:border-orange-300';
export const label = 'block text-xs font-semibold text-slate-600 mb-1';
export const filterLabel = 'text-xs font-semibold uppercase text-slate-500';

export function Notice({ tone = 'red', children }) {
  const t = {
    red: 'border-rose-200 bg-rose-50 text-rose-800',
    rose: 'border-rose-200 bg-rose-50 text-rose-800',
    amber: 'border-amber-200 bg-amber-50 text-amber-800',
    emerald: 'border-emerald-200 bg-emerald-50 text-emerald-800',
    sky: 'border-sky-100 bg-sky-50/70 text-sky-900',
    slate: 'border-slate-200 bg-slate-50 text-slate-700',
  };
  return <div className={`rounded-xl border text-sm px-4 py-3 ${t[tone] || t.slate}`}>{children}</div>;
}

/** The Issues Log dialog shell. */
export function Modal({ open, onClose, title, children, wide = false }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4" onClick={onClose}>
      <div className={`bg-white rounded-2xl border border-slate-200 shadow-xl p-6 w-full ${wide ? 'max-w-4xl' : 'max-w-lg'} max-h-[90vh] overflow-y-auto`} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-4 mb-4">
          <h2 className="text-lg font-semibold text-slate-900">{title}</h2>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-700 text-xl leading-none" aria-label="Close">×</button>
        </div>
        {children}
      </div>
    </div>
  );
}

export function Empty({ children }) {
  return <div className="bg-white rounded-2xl border border-slate-200 shadow-sm py-12 text-center text-slate-500 text-sm">{children}</div>;
}

/** A table inside a white card, with the Issues Log header row. */
export function Table({ head, children, className = '', flat = false }) {
  const inner = (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 border-b border-slate-200">
          <tr className="text-left text-slate-700 font-medium">
            {head.map((h, i) => <th key={i} className={`py-3 px-3 text-xs font-semibold text-slate-600 whitespace-nowrap ${h.right ? 'text-right' : ''}`}>{h.label ?? h}</th>)}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">{children}</tbody>
      </table>
    </div>
  );
  if (flat) return <div className={`rounded-xl border border-slate-200 overflow-hidden ${className}`}>{inner}</div>;
  return <div className={`bg-white rounded-2xl border border-slate-200 shadow-md overflow-hidden ${className}`}>{inner}</div>;
}

export function StatusPill({ status }) {
  const map = {
    submitted: ['amber', 'Pending review'], resubmitted: ['amber', 'Resubmitted'], changes_requested: ['rose', 'Changes requested'],
    approved: ['emerald', 'Approved'], held: ['sky', 'Held for review'], destroyed: ['slate', 'Destroyed'],
    pending: ['orange', 'Open'], used: ['emerald', 'Used'], revoked: ['slate', 'Revoked'], expired: ['slate', 'Expired'], draft: ['amber', 'Draft'], final: ['emerald', 'Final'],
  };
  const [tone, text] = map[status] || ['slate', String(status || '').replace(/_/g, ' ')];
  return <Pill tone={tone}>{text}</Pill>;
}

/** An option the user picks with a radio, as a soft card. */
export function OptionCard({ checked, onChange, disabled, title, children, tone = 'orange' }) {
  const on = tone === 'emerald' ? 'border-emerald-300 bg-emerald-50/50' : tone === 'rose' ? 'border-rose-300 bg-rose-50/50' : 'border-orange-300 bg-orange-50/50';
  return (
    <label className={`flex items-start gap-3 p-3 rounded-xl border ${disabled ? 'opacity-50' : 'cursor-pointer'} ${checked ? on : 'border-slate-200 hover:border-slate-300'}`}>
      <input type="radio" className="mt-0.5 accent-orange-600" checked={checked} onChange={onChange} disabled={disabled} />
      <span className="flex-1 min-w-0">
        <span className="block text-sm font-semibold text-slate-900">{title}</span>
        {children}
      </span>
    </label>
  );
}
